/**
 * Copied from AdminPortal.UI/src/components/Unauthorized (same layout, icons
 * and banner) - shown when the app-access group gate denies a signed-in
 * account. Contact line intentionally stays generic ("IT support"), not a
 * team name - CISO feedback on the security demo: public-facing pages should
 * not name anything internal to MRO.
 */
import { Button, Typography, Paper, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { BackIcon } from './BackIcon';
import { RefreshIcon } from './RefreshIcon';
import { InfoIcon } from './InfoIcon';
import { UnauthorizedBanner } from './UnauthorizedBanner';

const CenteredContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '95vh',
  backgroundColor: '#F8F9FB',
  padding: '3.5px',
}));

const StyledPaper = styled(Paper)(() => ({
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  boxShadow: '0px 2px 6px #00000014',
  borderRadius: '12px',
  padding: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  maxWidth: '100%',
}));

const Content = styled('div')(() => ({
  flex: 1,
  textAlign: 'left',
}));

const NotesBox = styled(Box)(() => ({
  background: '#D6E7F5 0% 0% no-repeat padding-box',
  borderRadius: '6px',
  padding: '12px 16px',
  marginTop: '16px',
  display: 'flex',
  alignItems: 'start',
  gap: '8px',
}));

const StyledButton = styled(Button)(() => ({
  background: '#FFFFFF08 0% 0% no-repeat padding-box',
  border: '1px solid #0153FF',
  borderRadius: '6px',
  font: 'normal normal 500 16px/28px Inter',
  color: '#0153FF',
  textTransform: 'uppercase',
  padding: '6px 20px',
  '&:hover': {
    backgroundColor: '#F0F5FF',
    borderColor: '#0142CC',
  },
}));

const Unauthorized = () => {
  const navigate = useNavigate();

  const refreshLoginSession = () => {
    sessionStorage.clear();
    window.location.reload();
    setTimeout(() => {
      window.location.reload();
    }, 200);
  };

  return (
    <CenteredContainer>
      <StyledPaper>
        {/* Left side placeholder for SVG (replace later with actual image) */}
        <Box sx={{ flex: 1 }}>
          <UnauthorizedBanner />
        </Box>

        {/* Right side content */}
        <Content>
          <Typography
            sx={{
              font: 'normal normal 600 42px/52px Inter',
              color: '#122455',
              marginBottom: '8px',
            }}
          >
            Unauthorized Access
          </Typography>

          <Typography
            sx={{
              font: 'normal normal normal 18px/26px Inter',
              color: '#4B555C',
              marginBottom: '8px',
            }}
          >
            You do not have the required permissions to view this page.
          </Typography>

          <Typography
            sx={{
              font: 'normal normal normal 18px/26px Inter',
              color: '#4B555C',
              marginBottom: '16px',
            }}
          >
            Please contact IT support to request access.
          </Typography>

          <NotesBox>
            <InfoIcon />
            <Typography
              sx={{
                font: 'normal normal normal 16px/24px Inter',
                color: '#122455',
              }}
            >
              <b>Note:</b> If access was recently granted, please refresh the
              login session to get your updated roles.
            </Typography>
          </NotesBox>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <StyledButton startIcon={<BackIcon />} onClick={() => navigate(-1)}>
              Go Back
            </StyledButton>
            <StyledButton
              startIcon={<RefreshIcon />}
              onClick={refreshLoginSession}
            >
              Refresh Login
            </StyledButton>
          </Box>
        </Content>
      </StyledPaper>
    </CenteredContainer>
  );
};

export default Unauthorized;
